import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30 * 1000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Handler de sessao invalida. Registrado pelo AuthContext, que e quem sabe
// derrubar o login. Fica fora do React porque o interceptor roda fora dele.
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null) => {
  onUnauthorized = handler;
};

/**
 * Status que, quando retornados pelo endpoint de perfil do proprio usuario,
 * indicam que ele nao esta mais autorizado (401), foi bloqueado (403) ou
 * excluido (404). Nesses casos fazemos o logout automatico usando o mesmo
 * processo do botao de sair. Mesma regra adotada no spott-cms.
 */
const SELF_PROFILE_LOGOUT_STATUSES = [401, 403, 404];

/**
 * Consulta de saldo. Sem o 404 de proposito: no /userprofile o 404 significa
 * "usuario excluido", mas aqui pode significar apenas "carteira inexistente"
 * para um usuario valido - deslogar nesse caso seria um falso positivo.
 */
const BALANCE_LOGOUT_STATUSES = [401, 403];

/** Ultimo segmento da URL, sem query string nem barra final. */
const lastSegmentOf = (rawUrl?: string): string => {
  if (!rawUrl) return '';
  const path = rawUrl.split('?')[0].replace(/\/+$/, '');
  return path.substring(path.lastIndexOf('/') + 1).toLowerCase();
};

/**
 * Verifica se a URL da requisicao e o endpoint de perfil do proprio usuario
 * (`/userprofile`). Precisa ser o segmento final para NAO casar com endpoints
 * administrativos como `/UserProfile/{id}/tags`, cujo 403 significa apenas
 * falta de permissao para aquela acao - e nao que o usuario foi bloqueado.
 */
const isSelfProfileUrl = (rawUrl?: string): boolean =>
  lastSegmentOf(rawUrl) === 'userprofile';

/**
 * Verifica se e a CONSULTA de saldo (`GET /wallet/Balance`). O metodo importa:
 * `POST /wallet/Balance` adiciona credito, e um erro ali e falha de pagamento,
 * nao de sessao - deslogar no meio de uma recarga esconderia o erro real.
 */
const isBalanceQueryUrl = (rawUrl?: string, method?: string): boolean =>
  lastSegmentOf(rawUrl) === 'balance' &&
  (method ?? 'get').toLowerCase() === 'get';

/**
 * Indica que este erro derrubou a sessao. As telas usam isso para NAO exibir
 * o toast de "falha ao carregar" - o usuario ja esta sendo redirecionado para
 * o login, entao a mensagem de erro so confundiria.
 */
export const isForcedLogoutError = (error: any): boolean =>
  error?.isForcedLogout === true;

/** Decide se a resposta de erro deve derrubar a sessao. */
const shouldForceLogout = (
  rawUrl?: string,
  method?: string,
  status?: number
): boolean => {
  if (typeof status !== 'number') return false;
  if (isSelfProfileUrl(rawUrl)) {
    return SELF_PROFILE_LOGOUT_STATUSES.includes(status);
  }
  if (isBalanceQueryUrl(rawUrl, method)) {
    return BALANCE_LOGOUT_STATUSES.includes(status);
  }
  return false;
};

api.interceptors.request.use(
  (config) => {
    // Cancela requisições quando a página não está visível (tela bloqueada/background)
    // Exceto para requisições críticas que devem continuar
    const isCriticalRequest = 
      config.url?.includes('/auth') || 
      config.url?.includes('/login') ||
      config.url?.includes('/logout') ||
      config.method === 'post' || 
      config.method === 'put' || 
      config.method === 'delete';

    if (!isCriticalRequest && document.hidden) {
      console.log('Requisição cancelada - página não visível:', config.url);
      return Promise.reject({
        message: 'Page not visible',
        config,
        code: 'PAGE_HIDDEN',
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url;

    // Usuario sem autorizacao/bloqueado/excluido: forca o logout.
    // Restrito aos status acima de proposito - erro de rede ou timeout nao
    // pode deslogar o usuario.
    if (shouldForceLogout(url, error?.config?.method, status)) {
      console.log(
        'Sessao invalida em ' + url + ' (status ' + status + ') - efetuando logout'
      );

      // Marca o erro para as telas suprimirem o toast de falha.
      if (error && typeof error === 'object') {
        error.isForcedLogout = true;
      }

      onUnauthorized?.();
    }

    return Promise.reject(error);
  }
);

export default {
  defaults: api.defaults,
  get: api.get,
  post: api.post,
  put: api.put,
  delete: api.delete,
  patch: api.patch,
};
